// React
import { useState } from "react";

// React redux
import { useDispatch } from "react-redux";

// @mui material components
import { CircularProgress, FormHelperText, Grid } from "@mui/material";

// Material Dashboard 2 React example components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PhoneInput from "components/PhoneInput";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Validator
import { isEmpty } from "validator";

// Service
import { update } from "services/user/users.services";

// Constants
import { validField, validProfileForm as validForm } from "constants/utils/user/validator";
import { errorMessage } from "constants/utils/user/errors.messages";
import { SERVER_ERROR } from "constants/messages/error";

const formData = (fields) => {
  const data = {
    id: fields.id.value,
    email: fields.email.value,
    fullname: fields.fullname.value,
    tel: fields.tel.value,
  };
  if (fields.plainPassword.value) data.plainPassword = fields.plainPassword.value;
  return data;
};

function FormEdit({ user, finished }) {
  const [fields, setFields] = useState({
    id: { value: user.id, error: "" },
    email: { value: user.email, error: "" },
    fullname: { value: user.fullname, error: "" },
    tel: { value: user.tel, error: "" },
    plainPassword: { value: "", error: "" },
    confirmPassword: { value: "", error: "" },
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatchApi = useDispatch();

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (error) setError("");
    setFields((f) => ({
      ...f,
      [name]: {
        value,
        error: !validField(name, value, fields.plainPassword.value) ? errorMessage[name] : "",
      },
    }));
  };

  const handleBlur = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (isEmpty(value)) setFields((f) => ({ ...f, [name]: { ...f[name], error: "" } }));
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!validForm(fields)) {
      Object.entries(fields).forEach(([name, { value }]) =>
        setFields((f) => ({
          ...f,
          [name]: {
            value,
            error: !validField(name, value, fields.plainPassword.value) ? errorMessage[name] : "",
          },
        }))
      );
      return;
    }
    if (error) setError("");
    Object.keys(fields).forEach((name) =>
      setFields((f) => ({
        ...f,
        [name]: {
          ...f[name],
          error: "",
        },
      }))
    );
    setLoading(true);
    update(formData(fields))
      .then(({ data }) => dispatchApi({ type: "CURRENT_USER", data }))
      .catch((err) => {
        if (err.request.status) {
          const { violations } = err.response.data;
          violations.forEach((violation) =>
            setFields((f) => ({
              ...f,
              [violation.propertyPath]: { ...f[violation.propertyPath], error: violation.message },
            }))
          );
        } else setError(SERVER_ERROR);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MDBox
      component="form"
      role="form"
      onSubmit={handleUpdate}
      noValidate
      autoComplete="off"
      fullWidth
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <MDBox mb={2}>
            <MDInput
              sx={{ fontSize: "0.2em !important" }}
              required
              error={Boolean(fields.fullname.error)}
              name="fullname"
              type="text"
              label="Nom et Prénom"
              value={fields.fullname.value}
              disabled={loading}
              placeholder="Saisir le nom et prénom"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              variant="standard"
            />
            {fields.fullname.error && (
              <FormHelperText error>{fields.fullname.error}</FormHelperText>
            )}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <MDInput
              required
              error={Boolean(fields.email.error)}
              name="email"
              type="email"
              label="Email"
              value={fields.email.value}
              disabled={loading}
              placeholder="Saisir un adresse email valide"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              variant="standard"
            />
            {fields.email.error && <FormHelperText error>{fields.email.error}</FormHelperText>}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <PhoneInput
              error={Boolean(fields.tel.error)}
              value={fields.tel.value}
              disabled={loading}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="standard"
            />
            {fields.tel.error && <FormHelperText error>{fields.tel.error}</FormHelperText>}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <MDInput
              error={Boolean(fields.plainPassword.error)}
              name="plainPassword"
              type="password"
              label="Mot de passe"
              value={fields.plainPassword.value}
              disabled={loading}
              placeholder="Saisir votre mot de passe"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              variant="standard"
              autoComplete="off"
            />
            {fields.plainPassword.error && (
              <FormHelperText error>{fields.plainPassword.error}</FormHelperText>
            )}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox mb={2}>
            <MDInput
              error={Boolean(fields.confirmPassword.error)}
              name="confirmPassword"
              type="password"
              label="Confirmation du mot de passe"
              value={fields.confirmPassword.value}
              disabled={loading}
              placeholder="Confirmer votre mot de passe"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              variant="standard"
            />
            {fields.confirmPassword.error && (
              <FormHelperText error>{fields.confirmPassword.error}</FormHelperText>
            )}
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={2} mb={1}>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </MDBox>
      <MDButton onClick={finished} disabled={loading} variant="text" color="info" size="small">
        Cacher
      </MDButton>
      <MDButton type="submit" disabled={loading} variant="text" color="info" size="small">
        Valider
        {loading && <CircularProgress size="1.5em" color="info" sx={{ position: "absolute" }} />}
      </MDButton>
    </MDBox>
  );
}

FormEdit.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object]).isRequired,
  finished: PropTypes.func.isRequired,
};

export default FormEdit;
